import lamejs from '@breezystack/lamejs';

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

const bufferToWav = async (buffer, numChannels, sampleRate) => {
  const length = buffer.length * numChannels * 2;
  const wav = new ArrayBuffer(44 + length);
  const view = new DataView(wav);

  // WAV Header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + length, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, length, true);

  // WAV Data
  const offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(
        -1,
        Math.min(1, buffer.getChannelData(channel)[i])
      );
      view.setInt16(
        offset + (i * numChannels + channel) * 2,
        sample * 0x7fff,
        true
      );
    }
  }

  return new Blob([wav], { type: 'audio/wav' });
};

const decodeAudioData = async audioBlob => {
  // ✅ VERIFICAÇÃO 1: Blob não vazio
  if (!audioBlob || audioBlob.size === 0) {
    console.error('[MP3Utils] ❌ ERRO: Blob vazio ou inválido!');
    throw new Error('Blob de áudio está vazio');
  }

  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const arrayBuffer = await audioBlob.arrayBuffer();
    
    // ✅ VERIFICAÇÃO 2: ArrayBuffer não vazio
    if (arrayBuffer.byteLength === 0) {
      console.error('[MP3Utils] ❌ ERRO: ArrayBuffer está vazio!');
      throw new Error('ArrayBuffer vazio após conversão');
    }
    
    const audioData = await audioContext.decodeAudioData(arrayBuffer);
    
    // ✅ FECHAR AUDIOCONTEXT
    await audioContext.close();
    
    return audioData;
    
  } catch (error) {
    console.error('[MP3Utils] ❌ === ERRO NA DECODIFICAÇÃO ===');
    console.error('[MP3Utils] Tipo do erro:', error.constructor.name);
    console.error('[MP3Utils] Mensagem:', error.message);
    console.error('[MP3Utils] Stack:', error.stack);
    
    // ✅ DIAGNÓSTICO ADICIONAL
    if (error.name === 'EncodingError' || error.message.includes('decode')) {
      console.error('[MP3Utils] 💡 DIAGNÓSTICO: Formato de áudio não reconhecido pelo navegador');
      console.error('[MP3Utils] 💡 Blob type:', audioBlob.type);
      console.error('[MP3Utils] 💡 Sugestão: O MediaRecorder pode ter gerado formato incompatível');
    }
    
    throw new Error(`Falha ao decodificar áudio: ${error.message}`);
  }
};

export const convertToWav = async audioBlob => {
  const audioBuffer = await decodeAudioData(audioBlob);
  return bufferToWav(
    audioBuffer,
    audioBuffer.numberOfChannels,
    audioBuffer.sampleRate
  );
};

export const encodeToMP3 = (channels, sampleRate, samples, bitrate = 128) => {


  try {
    const outputBuffer = [];
    
    const encoder = new lamejs.Mp3Encoder(channels, sampleRate, bitrate);
    
    const maxSamplesPerFrame = 1152;
    
    // ✅ PARA STEREO (2 CANAIS): SEPARAR OS CANAIS
    if (channels === 2) {
      
      const samplesPerChannel = samples.length / 2;
      const leftChannel = new Int16Array(samplesPerChannel);
      const rightChannel = new Int16Array(samplesPerChannel);
      
      // Deinterleave: samples[0,2,4...] = left, samples[1,3,5...] = right
      for (let i = 0; i < samplesPerChannel; i++) {
        leftChannel[i] = samples[i * 2];
        rightChannel[i] = samples[i * 2 + 1];
      }
      
    
      let frameCount = 0;
      for (let offset = 0; offset < samplesPerChannel; offset += maxSamplesPerFrame) {
        const sliceEnd = Math.min(offset + maxSamplesPerFrame, samplesPerChannel);
        
        const leftSlice = leftChannel.subarray(offset, sliceEnd);
        const rightSlice = rightChannel.subarray(offset, sliceEnd);
        
        const mp3Buffer = encoder.encodeBuffer(leftSlice, rightSlice);

        if (mp3Buffer.length > 0) {
          outputBuffer.push(new Int8Array(mp3Buffer));
          frameCount++;
        }
      }
      
      
    } else {
    
      let frameCount = 0;
      for (let offset = 0; offset < samples.length; offset += maxSamplesPerFrame) {
        const sliceEnd = Math.min(offset + maxSamplesPerFrame, samples.length);
        const sampleSlice = samples.subarray(offset, sliceEnd);
        
        const mp3Buffer = encoder.encodeBuffer(sampleSlice);

        if (mp3Buffer.length > 0) {
          outputBuffer.push(new Int8Array(mp3Buffer));
          frameCount++;
        }
      }
      
    }

    const remainingData = encoder.flush();
    if (remainingData.length > 0) {
      outputBuffer.push(new Int8Array(remainingData));
    }

    return new Blob(outputBuffer, { type: 'audio/mp3' });
    
  } catch (error) {
    console.error('[MP3Utils] ❌ === ERRO NO ENCODING ===');
    console.error('[MP3Utils] Mensagem:', error.message);
    console.error('[MP3Utils] Stack:', error.stack);
    throw error;
  }
};

export const convertToMp3 = async (audioBlob, bitrate = 128) => {

  try {
    // ✅ STEP 0: VALIDAÇÃO INICIAL
    if (!audioBlob || audioBlob.size === 0) {
      console.error('[MP3Utils] ❌ STEP 0 FALHOU: Blob vazio');
      throw new Error('Áudio vazio - nada para converter');
    }
    const audioBuffer = await decodeAudioData(audioBlob);
    
    // ✅ STEP 2: VALIDAR AUDIOBUFFER
    if (!audioBuffer || audioBuffer.length === 0) {
      console.error('[MP3Utils] ❌ STEP 2 FALHOU: AudioBuffer vazio');
      throw new Error('AudioBuffer vazio após decodificação');
    }

    const totalSamples = audioBuffer.length * audioBuffer.numberOfChannels;
    
    const samples = new Int16Array(totalSamples);
    
    let offset = 0;
    let minSample = 0;
    let maxSample = 0;
    
    for (let i = 0; i < audioBuffer.length; i += 1) {
      for (let channel = 0; channel < audioBuffer.numberOfChannels; channel += 1) {
        const sample = Math.max(
          -1,
          Math.min(1, audioBuffer.getChannelData(channel)[i])
        );
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        samples[offset] = intSample;
        
        // Track min/max para diagnóstico
        if (intSample < minSample) minSample = intSample;
        if (intSample > maxSample) maxSample = intSample;
        
        offset += 1;
      }
    }

    // ✅ VERIFICAR SE TEM ÁUDIO REAL
    if (minSample === 0 && maxSample === 0) {
      console.warn('[MP3Utils] ⚠️ AVISO: Todos os samples são zero (silêncio)');
    }

    const mp3Blob = encodeToMP3(
      audioBuffer.numberOfChannels,
      audioBuffer.sampleRate,
      samples,
      bitrate
    );

    // ✅ STEP 5: VALIDAR OUTPUT
    if (mp3Blob.size === 0) {
      console.error('[MP3Utils] ❌ STEP 5 FALHOU: MP3 Blob vazio');
      throw new Error('MP3 resultante está vazio');
    }


    return mp3Blob;

  } catch (error) {
    console.error('[MP3Utils] ❌ ========================================');
    console.error('[MP3Utils] ❌ === CONVERSÃO FALHOU ===');
    console.error('[MP3Utils] ❌ ========================================');
    console.error('[MP3Utils] ❌ Tipo:', error.constructor.name);
    console.error('[MP3Utils] ❌ Mensagem:', error.message);
    console.error('[MP3Utils] ❌ Stack completo:', error.stack);
    
    // ✅ RE-LANÇAR COM CONTEXTO
    throw new Error(`Conversão MP3 falhou: ${error.message}`);
  }
};

export const convertAudio = async (inputBlob, outputFormat, bitrate = 128) => {
  let audio;
  if (outputFormat === 'audio/wav') {
    audio = await convertToWav(inputBlob);
  } else if (outputFormat === 'audio/mp3') {
    audio = await convertToMp3(inputBlob, bitrate);
  } else {
    throw new Error('Unsupported output format');
  }
  return audio;
};